/**
 * 2D DXF of a floor plan, derived from the exact SVG the editor renders:
 * renderPlanExport produces the plan's export SVG (a closed set of elements —
 * line / polyline / polygon / path / text, grouped under data-layer tags, a
 * group optionally placed by a translate + rotate transform), and this module
 * converts each element to a DXF entity in plan feet. Because the source is
 * the rendered drawing itself, the DXF always matches the editor — any new
 * glyph added to the 2D view exports automatically, and a whole assembled
 * level (rooms placed by transform) converts through the very same path.
 *
 * Coordinates are feet ($INSUNITS), plan centered at the origin, y up — the
 * same frame as the STEP/STL exports, so the plan overlays the 3D model.
 * Symbolic stroke widths and paint-order wipes (background-colored lines that
 * mask the wall line across an opening) are rendering devices with no CAD
 * meaning: strokes export as thin lines and wipes are dropped.
 */

import { FloorPlanRoom, Point } from '@kaiizen/kai-floor-plan';
import {
  ALL_PLAN_LAYERS,
  dxfDocument,
  type DxfEntity,
  type PlanLayers,
  screenToModel,
  type Viewport,
} from '@kaiizen/kai-floor-plan-editor-core';
import { canvasBg } from '../../styles';
import { renderPlanExport } from './exportPlanSvg';

/** ACI colors distinguishing the plan's semantic layers in any CAD viewer. */
const LAYER_COLORS: Record<string, number> = {
  FLOOR: 8, // dark gray
  FLOORING: 9, // light gray
  WALL: 7, // white/black
  'WALL-BAND': 7, // white/black
  DOOR: 30, // orange
  WINDOW: 4, // cyan
  OPENING: 8, // dark gray
  COUNTER: 3, // green
  OBJECT: 5, // blue
  'ROOM-NAME': 7, // white/black
};

interface SvgTag {
  name: string;
  closing: boolean;
  attrs: Record<string, string>;
  /** Inner text of a <text> element. */
  text?: string;
}

const decodeEntities = (text: string): string =>
  text.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#x27;/g, "'");

/** Scans the export SVG (our own generated subset — no nesting surprises).
 *  Our <text> elements hold a single text node, so the nth text-content match
 *  belongs to the nth opening <text> tag. */
const svgTags = (svg: string): SvgTag[] => {
  const tags: SvgTag[] = [];
  const textContents = [...svg.matchAll(/<text(?:"[^"]*"|[^">])*>([^<]*)<\/text>/g)].map(m => decodeEntities(m[1]));
  let textIndex = 0;
  for (const [, closeMark, name, attrBody] of svg.matchAll(/<(\/?)([a-zA-Z]+)((?:"[^"]*"|[^">])*)\/?>/g)) {
    const attrs: Record<string, string> = {};
    for (const [, key, value] of attrBody.matchAll(/([a-zA-Z][a-zA-Z0-9-]*)="([^"]*)"/g)) attrs[key] = value;
    const closing = closeMark === '/';
    const text = name === 'text' && !closing ? textContents[textIndex++] : undefined;
    tags.push({ name, closing, attrs, text });
  }
  return tags;
};

const parsePoints = (points: string): Point[] =>
  points.trim().split(/\s+/).map(pair => {
    const [x, y] = pair.split(',').map(Number);
    return { x, y };
  });

/** Path data rings: our paths only ever contain M/L/Z commands (see ringsPath). */
const parsePathRings = (d: string): Point[][] => {
  const rings: Point[][] = [];
  let current: Point[] = [];
  for (const [, command, coordinates] of d.matchAll(/([MLZ])([^MLZ]*)/g)) {
    if (command === 'Z') {
      if (current.length >= 2) rings.push(current);
      current = [];
      continue;
    }
    const numbers = coordinates.trim().split(/[\s,]+/).filter(Boolean).map(Number);
    for (let i = 0; i + 1 < numbers.length; i += 2) current.push({ x: numbers[i], y: numbers[i + 1] });
  }
  if (current.length >= 2) rings.push(current);
  return rings;
};

type PointMap = (p: Point) => Point;

const IDENTITY: PointMap = p => p;

/**
 * The point map of an SVG `transform` attribute from our own generated subset:
 * a sequence of `translate(tx ty)` and `rotate(deg [cx cy])`, applied to a
 * point right-to-left as SVG does.
 */
const parseSvgTransform = (transform: string | undefined): PointMap => {
  if (!transform) return IDENTITY;
  const steps: PointMap[] = [];
  for (const [, fn, body] of transform.matchAll(/(translate|rotate)\(([^)]*)\)/g)) {
    const n = body.trim().split(/[\s,]+/).map(Number);
    if (fn === 'translate') {
      const [tx, ty = 0] = n;
      steps.push(p => ({ x: p.x + tx, y: p.y + ty }));
    } else {
      const [deg, cx = 0, cy = 0] = n;
      const rad = (deg * Math.PI) / 180, cos = Math.cos(rad), sin = Math.sin(rad);
      steps.push(p => {
        const dx = p.x - cx, dy = p.y - cy;
        return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
      });
    }
  }
  return p => steps.reduceRight((q, step) => step(q), p);
};

/**
 * Every drawn element of an export SVG as a DXF entity: element coordinates
 * (through any enclosing group transforms) are mapped by `toPlan` into plan
 * feet; `scale` (export px per foot) sizes text.
 */
export const svgToDxfEntities = (svg: string, toPlan: PointMap, scale: number): DxfEntity[] => {
  const entities: DxfEntity[] = [];
  const groups: { layer: string; map: PointMap }[] = [];
  const top = () => groups[groups.length - 1] ?? { layer: 'PLAN', map: IDENTITY };
  const project = (p: Point): Point => toPlan(top().map(p));
  for (const tag of svgTags(svg)) {
    if (tag.name === 'g') {
      if (tag.closing) groups.pop();
      else {
        const parent = top(), own = parseSvgTransform(tag.attrs.transform);
        groups.push({ layer: tag.attrs['data-layer']?.toUpperCase() ?? parent.layer, map: own === IDENTITY ? parent.map : p => parent.map(own(p)) });
      }
      continue;
    }
    if (tag.closing) continue;
    const layer = top().layer;
    const dashed = !!tag.attrs['stroke-dasharray'] || undefined;
    if (tag.name === 'line') {
      // Background-colored lines are paint-order wipes, not geometry.
      if (tag.attrs.stroke === canvasBg) continue;
      const points = [{ x: +tag.attrs.x1, y: +tag.attrs.y1 }, { x: +tag.attrs.x2, y: +tag.attrs.y2 }];
      entities.push({ kind: 'polyline', layer, points: points.map(project), closed: false, dashed });
    } else if (tag.name === 'polyline' || tag.name === 'polygon') {
      entities.push({ kind: 'polyline', layer, points: parsePoints(tag.attrs.points).map(project), closed: tag.name === 'polygon', dashed });
    } else if (tag.name === 'path') {
      for (const ring of parsePathRings(tag.attrs.d)) {
        entities.push({ kind: 'polyline', layer, points: ring.map(project), closed: true, dashed });
      }
    } else if (tag.name === 'text' && tag.text) {
      const fontPx = Number(/font-size:\s*([\d.]+)px/.exec(tag.attrs.style ?? '')?.[1] ?? 12);
      entities.push({ kind: 'text', layer, text: tag.text, position: project({ x: +tag.attrs.x, y: +tag.attrs.y }), height: fontPx / scale });
    }
  }
  return entities;
};

/** Export px → plan feet, centered on `center` with y up (the STEP/STL frame). */
export const exportToPlan = (viewport: Viewport, center: Point): PointMap => p => {
  const model = screenToModel(viewport, p);
  return { x: model.x - center.x, y: center.y - model.y };
};

/** The plan's DXF document with the semantic layer colors. */
export const planDxfDocument = (entities: DxfEntity[]): string => dxfDocument(entities, LAYER_COLORS);

export const roomToPlanDxf = (room: FloorPlanRoom, layers: PlanLayers = ALL_PLAN_LAYERS): string => {
  const { svg, viewport } = renderPlanExport(room, layers);
  return planDxfDocument(svgToDxfEntities(svg, exportToPlan(viewport, { x: room.width / 2, y: room.length / 2 }), viewport.scale));
};
